// The overview's map (?view=map): the organisation's sites where they are, each in the worst status of its
// equipment, and its trucks where their last GPS fix put them. Everyone who sees the overview sees it (viewers too):
// it shows the same statuses. A site without a location is listed below the map; Owners and Admins place it in
// Settings → Sites. The map itself (MapLibre, OpenFreeMap tiles) loads only when this view opens (@cck/map).

import type { AlarmSummary, Asset, AssetStatus, AssetStatusCode, Site } from '@cck/schema';
import {
  countStatuses, type MapLabels, MapLegend, type MapPoint, MapPointList, PROBLEMS, StatusMap, STATUS_ORDER, worstStatus,
} from '@cck/map';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { AssetTypeLabel, SeverityBadge, StatusBadge } from '../components/domain.tsx';
import { Badge, Button, Card, cx, EmptyState, LinkButton, Select } from '../components/ui.tsx';
import { useI18n } from '../i18n/index.ts';
import { formatAgo, formatTemp } from '../lib/format.ts';

type StatusFilter = '' | 'problems' | AssetStatusCode;

const siteKey = (id: string) => `site:${id}`;
const vehicleKey = (id: string) => `vehicle:${id}`;

export function OverviewMap({ sites, assets, status, probeLabel, alarms, admin }: {
  sites: Site[]; assets: Asset[]; status: Map<string, AssetStatus>; probeLabel: Map<string, string>;
  alarms: AlarmSummary[]; admin: boolean;
}) {
  const { t } = useI18n();
  const [filter, setFilter] = useState<StatusFilter>('');
  const [selected, setSelected] = useState<string | null>(null);
  const labels = useMemo<MapLabels>(() => ({
    region: t('map.region'), zoomIn: t('map.zoomIn'), zoomOut: t('map.zoomOut'), attribution: t('map.attribution'),
    unavailable: t('map.unavailable'), loading: t('map.loading'), site: t('map.site'), vehicle: t('map.vehicle'),
    status: Object.fromEntries(STATUS_ORDER.map((s) => [s, t(`status.${s}`)])) as Record<AssetStatusCode, string>,
  }), [t]);

  const statusOf = (a: Asset): AssetStatusCode => status.get(a.id)?.status ?? 'no_data';
  // Trucks with a known position are vehicles; every other asset belongs to its site's marker.
  const { points, groups, unplaced } = useMemo(() => {
    const groups = new Map<string, { site: Site | null; assets: Asset[] }>();
    const points: MapPoint[] = [];
    const siteName = new Map(sites.map((s) => [s.id, s.name]));
    for (const a of assets) {
      const pos = a.type === 'truck' ? status.get(a.id)?.position : null;
      if (pos) {
        groups.set(vehicleKey(a.id), { site: null, assets: [a] });
        points.push({ id: vehicleKey(a.id), kind: 'vehicle', lat: pos.lat, lon: pos.lon, status: statusOf(a), label: a.name,
          detail: siteName.get(a.site_id) ?? '' });
      }
    }
    const unplaced: { site: Site; status: AssetStatusCode }[] = [];
    for (const s of sites) {
      const mine = assets.filter((a) => a.site_id === s.id && !groups.has(vehicleKey(a.id)));
      const worst = worstStatus(mine.map(statusOf));
      if (s.latitude === null || s.longitude === null) {
        unplaced.push({ site: s, status: worst });
        continue;
      }
      groups.set(siteKey(s.id), { site: s, assets: mine });
      points.push({ id: siteKey(s.id), kind: 'site', lat: s.latitude, lon: s.longitude, status: worst, label: s.name,
        ...(s.address ? { detail: s.address.split('\n')[0]! } : {}) });
    }
    points.sort((a, b) => STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status) || a.label.localeCompare(b.label));
    return { points, groups, unplaced };
    // statusOf reads `status`
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sites, assets, status]);

  const shown = points.filter((p) => filter === '' || (filter === 'problems' ? PROBLEMS.includes(p.status) : p.status === filter));
  const current = selected && shown.some((p) => p.id === selected) ? selected : null;
  const group = current ? groups.get(current) : undefined;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <MapLegend labels={{ ...labels, title: t('map.legend') }} counts={countStatuses(points.map((p) => p.status))} />
        <Select aria-label={t('map.statusFilter')} value={filter} className="w-auto"
          onChange={(e) => setFilter(e.target.value as StatusFilter)}>
          <option value="">{t('map.allStatuses')}</option>
          <option value="problems">{t('map.problems')}</option>
          {STATUS_ORDER.map((s) => <option key={s} value={s}>{t(`status.${s}`)}</option>)}
        </Select>
      </div>

      {points.length === 0 ? (
        <EmptyState title={t('map.noPoints')} action={admin && <LinkButton to="settings/sites">{t('map.place')}</LinkButton>}>
          {t('map.noPointsHint')}
        </EmptyState>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
          <StatusMap points={shown} selected={current} onSelect={setSelected} labels={labels}
            className="h-[55vh] min-h-[320px] w-full overflow-hidden border border-slate-200" />
          <div className="flex min-w-0 flex-col gap-4">
            {group ? (
              <Details pointId={current!} group={group} status={status} probeLabel={probeLabel} alarms={alarms}
                siteName={sites.find((s) => s.id === group.assets[0]?.site_id)?.name ?? ''} onClose={() => setSelected(null)} />
            ) : <p className="text-sm text-slate-500">{t('map.choose')}</p>}
            <Card title={t('map.list')}>
              {shown.length === 0 ? <p className="text-sm text-slate-500">{t('map.nothing')}</p> : (
                <MapPointList points={shown} selected={current} onSelect={setSelected} labels={labels}
                  className="-mx-2 max-h-80 overflow-y-auto" />
              )}
            </Card>
          </div>
        </div>
      )}

      {unplaced.length > 0 && (
        <Card title={t('map.missing')} actions={admin && <LinkButton to="settings/sites" size="sm" variant="secondary">{t('map.place')}</LinkButton>}>
          <p className="mb-2 text-sm text-slate-500">{t('map.missingHint')}</p>
          <ul className="flex flex-wrap gap-2">
            {unplaced.map(({ site, status: s }) => (
              <li key={site.id} className="inline-flex items-center gap-2 border border-slate-200 bg-white px-2.5 py-1 text-sm">
                {site.name} <StatusBadge status={s} />
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

function Details({ pointId, group, status, probeLabel, alarms, siteName, onClose }: {
  pointId: string; group: { site: Site | null; assets: Asset[] }; status: Map<string, AssetStatus>;
  probeLabel: Map<string, string>; alarms: AlarmSummary[]; siteName: string; onClose: () => void;
}) {
  const { t, lang } = useI18n();
  const ids = new Set(group.assets.map((a) => a.id));
  const active = alarms.filter((a) => a.condition === 'active' && a.asset_id !== null && ids.has(a.asset_id));
  const counts = countStatuses(group.assets.map((a) => status.get(a.id)?.status ?? 'no_data'));
  const vehicle = pointId.startsWith('vehicle:') ? group.assets[0] : undefined;
  const position = vehicle ? status.get(vehicle.id)?.position : null;
  return (
    <section aria-labelledby="map-details" className="border border-slate-200 bg-white p-4"
      onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 id="map-details" className="truncate font-semibold text-slate-900">{group.site?.name ?? vehicle?.name}</h2>
          {group.site?.address && <p className="text-xs text-slate-500">{group.site.address}</p>}
          {vehicle && (
            <p className="text-xs text-slate-500">
              {t('map.vehicleOf', { site: siteName })}{position && <> · {t('overview.position', { ago: formatAgo(position.at, lang) })}</>}
            </p>
          )}
        </div>
        <Button size="sm" variant="ghost" onClick={onClose}>{t('map.close')}</Button>
      </div>
      {!vehicle && (
        <p className="mt-2 flex flex-wrap gap-1">
          {STATUS_ORDER.filter((s) => counts[s] > 0).map((s) => <Badge key={s} tone="slate">{t(`status.${s}`)} · {counts[s]}</Badge>)}
        </p>
      )}
      <h3 className="mt-4 text-xs font-semibold uppercase tracking-wide text-slate-500">{t('map.equipment')}</h3>
      {group.assets.length === 0 ? <p className="mt-1 text-sm text-slate-500">{t('map.noEquipment')}</p> : (
        <ul className="mt-1 divide-y divide-slate-100">
          {group.assets.map((a) => {
            const st = status.get(a.id);
            return (
              <li key={a.id} className="py-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <Link to={`assets/${a.id}`} className="font-medium text-brand-700 hover:underline">{a.name}</Link>
                    <p className="text-xs text-slate-500"><AssetTypeLabel type={a.type} /></p>
                  </div>
                  <StatusBadge status={st?.status ?? 'no_data'} />
                </div>
                {(st?.probes.length ?? 0) > 0 && (
                  <p className="mt-1 flex flex-wrap gap-x-3 text-sm text-slate-700">
                    {st!.probes.map((p) => (
                      <span key={p.probe_id}>
                        {st!.probes.length > 1 && <span className="mr-1 text-xs text-slate-500">{probeLabel.get(p.probe_id) ?? '—'}</span>}
                        <span className={cx('font-semibold tabular-nums', st!.status === 'alarm' && 'text-red-700')}>{formatTemp(p.value_c, lang)}</span>
                      </span>
                    ))}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {active.length > 0 && (
        <>
          <h3 className="mt-4 text-xs font-semibold uppercase tracking-wide text-slate-500">{t('map.alarms')}</h3>
          <ul className="mt-1 space-y-1">
            {active.map((a) => (
              <li key={a.id} className="flex flex-wrap items-center gap-2 text-sm">
                <SeverityBadge severity={a.severity} />
                <Link to={`alarms/${a.id}`} className="text-brand-700 hover:underline">{t(`alarmType.${a.type}`)}</Link>
                <span className="text-xs text-slate-500">{t('map.since', { ago: formatAgo(a.condition_since, lang) })}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
